import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { monitoringApi, type SystemAlert } from '../api/admin';

function when(value: string | null) {
  return value ? new Date(value).toLocaleString('en-GB') : '—';
}

/**
 * Operator view of what the background monitors have found.
 *
 * The point of the list is the top of it: unacknowledged and critical first. An alert stays open
 * until a person clears it, because the conditions here — a rate page that moved, a year with no
 * parameters — are ones where nobody noticing is the actual failure.
 */
export default function AlertsPanel() {
  const queryClient = useQueryClient();
  const [includeAcknowledged, setIncludeAcknowledged] = useState(false);

  const { data: alerts, isPending } = useQuery({
    queryKey: ['alerts', includeAcknowledged],
    queryFn: () => monitoringApi.alerts(includeAcknowledged),
  });

  const { data: sources } = useQuery({
    queryKey: ['legal-sources'],
    queryFn: monitoringApi.sources,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['alerts'] });
    void queryClient.invalidateQueries({ queryKey: ['legal-sources'] });
  };

  const acknowledge = useMutation({
    mutationFn: monitoringApi.acknowledge,
    onSuccess: invalidate,
  });

  const checkNow = useMutation({
    mutationFn: monitoringApi.checkNow,
    onSuccess: invalidate,
  });

  return (
    <>
      <section className="card">
        <div className="card__header">
          <h2>Alerts</h2>
          <div className="toolbar">
            <label className="check">
              <input
                type="checkbox"
                checked={includeAcknowledged}
                onChange={(e) => setIncludeAcknowledged(e.target.checked)}
              />
              <span>Show acknowledged</span>
            </label>
            <button
              type="button"
              className="btn"
              onClick={() => checkNow.mutate()}
              disabled={checkNow.isPending}
            >
              {checkNow.isPending ? 'Checking…' : 'Check now'}
            </button>
          </div>
        </div>

        {isPending && <p className="muted">Loading…</p>}

        {alerts?.length === 0 && (
          <p className="muted">Nothing open. The monitors have not found anything to act on.</p>
        )}

        {alerts?.map((alert) => (
          <AlertRow
            key={alert.id}
            alert={alert}
            busy={acknowledge.isPending}
            onAcknowledge={() => acknowledge.mutate(alert.id)}
          />
        ))}
      </section>

      <section className="card">
        <div className="card__header">
          <h2>Watched legal sources</h2>
        </div>

        <p className="muted">
          These pages are fingerprinted, never parsed. A change here opens an alert so a human can
          read it — applying a new rate is still a Draft, a diff and an activation.
        </p>

        <table className="table">
          <thead>
            <tr>
              <th>Source</th>
              <th>Last checked</th>
              <th>Last change</th>
              <th>Health</th>
            </tr>
          </thead>
          <tbody>
            {sources?.map((source) => (
              <tr key={source.id}>
                <td>
                  <strong>{source.description}</strong>
                  <br />
                  <a href={source.url} target="_blank" rel="noreferrer noopener">
                    {source.url}
                  </a>
                </td>
                <td>{when(source.lastCheckedAt)}</td>
                <td>{when(source.lastChangedAt)}</td>
                <td>
                  {!source.isEnabled ? (
                    <span className="muted">disabled</span>
                  ) : source.consecutiveFailures > 0 ? (
                    <span className="status-error">
                      {source.consecutiveFailures} failure(s)
                      {source.lastError ? `: ${source.lastError}` : ''}
                    </span>
                  ) : source.lastCheckedAt ? (
                    <span className="status-ok">ok</span>
                  ) : (
                    <span className="muted">not yet checked</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}

function AlertRow({
  alert,
  busy,
  onAcknowledge,
}: {
  alert: SystemAlert;
  busy: boolean;
  onAcknowledge: () => void;
}) {
  const severity = alert.severity.toLowerCase();

  return (
    <article className={`alert alert--${severity}`}>
      <div className="alert__head">
        <span className={`badge badge--${severity}`}>{alert.severity}</span>
        <h3>{alert.title}</h3>
        {alert.acknowledgedAt ? (
          <span className="muted">acknowledged {when(alert.acknowledgedAt)}</span>
        ) : (
          <button type="button" className="btn" onClick={onAcknowledge} disabled={busy}>
            Acknowledge
          </button>
        )}
      </div>

      <p>{alert.detail}</p>

      <p className="muted">
        First seen {when(alert.raisedAt)}, last seen {when(alert.lastSeenAt)}
        {/* A high count means it has been true for that many checks and nobody has looked. */}
        {alert.occurrences > 1 ? ` · seen ${alert.occurrences} times` : ''}
        {alert.url ? ' · ' : ''}
        {alert.url && (
          <a href={alert.url} target="_blank" rel="noreferrer noopener">
            {alert.url}
          </a>
        )}
      </p>
    </article>
  );
}
